import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { useAuth } from '../auth';
import { api } from '../lib/api';
import { getSocket } from '../lib/socket';
import { useUnread } from '../lib/use-unread';
import { PushPermissionBanner } from '../components/PushPermissionBanner';
import type { CreditRequest, CreditRequestPage, Stats, Store } from '../types';

type StatusFilter = 'all' | 'open' | 'processing' | 'closed';

export function DashboardPage() {
  const { me } = useAuth();
  const qc = useQueryClient();
  const [status, setStatus] = useState<StatusFilter>('all');
  const isStaff = me?.roles.includes('admin') || me?.roles.includes('customer_service');

  const list = useQuery({
    queryKey: ['credit-requests', { status }],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (status !== 'all') params.set('status', status);
      params.set('limit', '50');
      return api<CreditRequestPage>(`/api/v1/credit-requests?${params.toString()}`);
    },
  });

  const statsQ = useQuery({
    queryKey: ['stats'],
    queryFn: () => api<Stats>('/api/v1/stats'),
    enabled: !!isStaff,
    refetchInterval: 30_000,
  });

  const unreadQ = useUnread();
  const unreadRequests = unreadQ.data?.requests ?? [];
  const totalUnread = unreadQ.data?.totalUnread ?? 0;
  const unreadMap = useMemo(() => {
    const m: Record<string, number> = {};
    for (const r of unreadRequests) m[r.requestId] = r.count;
    return m;
  }, [unreadRequests]);

  useEffect(() => {
    const s = getSocket();
    const onChanged = () => qc.invalidateQueries({ queryKey: ['credit-requests'] });
    s.on('request:new', onChanged);
    s.on('request:status_changed', onChanged);
    s.on('request:closed', onChanged);
    s.on('request:reopened', onChanged);
    s.on('request:assigned', onChanged);
    return () => {
      s.off('request:new', onChanged);
      s.off('request:status_changed', onChanged);
      s.off('request:closed', onChanged);
      s.off('request:reopened', onChanged);
      s.off('request:assigned', onChanged);
    };
  }, [qc]);

  const itemsRaw = list.data?.items ?? [];
  const items = useMemo(() => {
    return [...itemsRaw].sort((a, b) => {
      const ua = unreadMap[a.id] ?? 0;
      const ub = unreadMap[b.id] ?? 0;
      if (ub !== ua) return ub - ua;
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
  }, [itemsRaw, unreadMap]);
  const stats = statsQ.data;
  const firstUnread = unreadRequests[0];

  return (
    <div className="space-y-5">
      <PushPermissionBanner />
      {!isStaff && me && me.stores[0] && <NewRequestCTA />}

      {!isStaff && totalUnread > 0 && firstUnread && (
        <Link
          to={`/request/${firstUnread.requestId}`}
          className="block bg-rose-50 border-2 border-rose-300 rounded-2xl p-4 text-rose-900 font-semibold hover:bg-rose-100 active:bg-rose-200 transition"
        >
          💬 Tienes {totalUnread} mensaje{totalUnread === 1 ? '' : 's'} sin leer · tap para ir al primero
        </Link>
      )}

      {isStaff && (
        <div className="grid grid-cols-2 sm:grid-cols-6 gap-3">
          <KpiCard label="Hoy" value={fmtN(stats?.todayTotal)} accent="text-brand-700" />
          <KpiCard label="Abiertas" value={fmtN(stats?.openCount)} accent="text-amber-600" />
          <KpiCard label="En proceso" value={fmtN(stats?.processingCount)} accent="text-sky-600" />
          <KpiCard label="Cerradas hoy" value={fmtN(stats?.closedToday)} accent="text-emerald-700" />
          <KpiCard label="Cierre prom." value={fmtDuration(stats?.avgClosureMinutes30d)} accent="text-slate-700" small />
          <KpiCard label="💬 Sin leer" value={fmtN(totalUnread)} accent={totalUnread > 0 ? 'text-rose-600' : 'text-slate-400'} />
        </div>
      )}

      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="px-4 py-3.5 border-b border-slate-100 flex items-center gap-2 overflow-x-auto">
          {(['all', 'open', 'processing', 'closed'] as StatusFilter[]).map((s) => (
            <button
              key={s}
              onClick={() => setStatus(s)}
              className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition ${
                status === s ? 'bg-brand-700 text-white shadow' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              {labels[s]}
            </button>
          ))}
        </div>

        {list.isLoading && <div className="p-8 text-center text-slate-400 text-base">Cargando…</div>}
        {list.isError && <div className="p-8 text-center text-rose-700 text-base">Error cargando solicitudes</div>}
        {!list.isLoading && items.length === 0 && (
          <div className="p-10 text-center text-slate-400">
            <div className="text-5xl mb-3">📭</div>
            <p className="text-base">No hay solicitudes en este filtro.</p>
          </div>
        )}

        <ul className="divide-y divide-slate-100">
          {items.map((r) => {
            const unread = unreadMap[r.id] ?? 0;
            return (
              <li key={r.id}>
                <Link
                  to={`/request/${r.id}`}
                  className={`flex items-center justify-between gap-3 px-5 py-4 transition ${
                    unread > 0
                      ? 'bg-rose-50 hover:bg-rose-100 active:bg-rose-200'
                      : 'hover:bg-slate-50 active:bg-slate-100'
                  }`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    {unread > 0 && (
                      <span
                        className="shrink-0 inline-flex items-center justify-center min-w-[32px] h-8 px-2 bg-rose-500 text-white text-base font-bold rounded-full shadow"
                        aria-label={`${unread} sin leer`}
                      >
                        {unread > 99 ? '99+' : unread}
                      </span>
                    )}
                    <div className="min-w-0">
                      <div className="font-bold text-slate-900 text-lg truncate">{r.claimNumber}</div>
                      <div className="text-base text-slate-500 truncate mt-0.5">
                        {r.store.name}
                        {r.agent ? ` · ${r.agent.name}` : ''}
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1.5 shrink-0">
                    <StatusPill status={r.status} />
                    {r.creditType === 'open_type' && r.status !== 'closed' && r.boxesAffected == null && (
                      <span className="text-xs uppercase font-bold px-2.5 py-1 rounded-full bg-amber-100 text-amber-800 whitespace-nowrap">
                        🛠️ Trabajando
                      </span>
                    )}
                    <div className="text-sm text-slate-400">
                      {format(new Date(r.createdAt), 'dd/MM HH:mm')}
                    </div>
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

function NewRequestCTA() {
  const nav = useNavigate();
  return (
    <button
      onClick={() => nav('/new')}
      className="w-full py-5 bg-brand-600 hover:bg-brand-700 active:bg-brand-800 text-white text-xl font-bold rounded-2xl shadow-md flex items-center justify-center gap-2"
    >
      <span className="text-2xl">+</span> Nueva solicitud
    </button>
  );
}

function KpiCard({ label, value, accent, small }: { label: string; value: string; accent: string; small?: boolean }) {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 px-5 py-4">
      <div className={`${small ? 'text-2xl' : 'text-4xl'} font-bold ${accent}`}>{value}</div>
      <div className="text-sm text-slate-500 uppercase tracking-wide font-semibold mt-1">{label}</div>
    </div>
  );
}

function fmtN(n: number | undefined): string {
  return n != null ? String(n) : '—';
}

function fmtDuration(min: number | null | undefined): string {
  if (min == null) return '—';
  if (min < 60) return `${Math.round(min)}m`;
  const h = Math.floor(min / 60);
  const m = Math.round(min - h * 60);
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

function StatusPill({ status }: { status: CreditRequest['status'] }) {
  const cls =
    status === 'open' ? 'bg-amber-100 text-amber-800' :
    status === 'processing' ? 'bg-sky-100 text-sky-800' :
    'bg-emerald-100 text-emerald-800';
  return (
    <span className={`text-xs uppercase font-bold px-3 py-1 rounded-full ${cls}`}>
      {labels[status as keyof typeof labels]}
    </span>
  );
}

const labels = {
  all: 'Todas',
  open: 'Abiertas',
  processing: 'En proceso',
  closed: 'Cerradas',
};

export type _store = Store;
export type _cr = CreditRequest;
